import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useEuropeUsaImport } from '@/api/recognition';
import type { EuropeUsaImportResultDto } from '@/api/types';
import { StepInstructions } from './StepInstructions';
import { StepUploadFile } from './StepUploadFile';
import { StepReview } from './StepReview';
import { StepConfirm } from './StepConfirm';

interface Props {
  onClose: () => void;
}

type WizardStep = 'instructions' | 'upload' | 'review' | 'confirm';

const STEPS: WizardStep[] = ['instructions', 'upload', 'review', 'confirm'];
const STEP_LABEL_KEYS: Record<WizardStep, string> = {
  instructions: 'bulk.stepInstructions',
  upload: 'bulk.stepUpload',
  review: 'bulk.stepReview',
  confirm: 'bulk.stepConfirm',
};

/** Wizard de carga masiva del padrón de usuarios (Europa/EE. UU.) por CSV. */
export function AdminBulkUsersWizardModal({ onClose }: Readonly<Props>) {
  const { t } = useTranslation('recognition');
  const importer = useEuropeUsaImport();
  const [step, setStep] = useState<WizardStep>('instructions');
  const [csv, setCsv] = useState('');
  const [ou, setOu] = useState('');
  const [previewResult, setPreviewResult] = useState<EuropeUsaImportResultDto | null>(null);

  const stepIndex = STEPS.indexOf(step);

  // createPortal a document.body: `.plataforma-main` (recognition-shell.css) lleva `isolation:
  // isolate`, que aísla el z-index de `.modal-overlay` dentro de su propio contexto de apilamiento
  // — nunca compite contra el z-index explícito del Sidebar, que queda pintado encima. Mismo fix ya
  // aplicado en BulkUploadModal.tsx/WinnersImportWizardModal.tsx.
  return createPortal((
    <div className="modal-overlay modal-overlay--fade" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="bulk-modal adm-bulk-wizard-modal" onClick={(event) => event.stopPropagation()}>
        <button className="rec-modal-close" onClick={onClose} aria-label={t('common.close')}><i className="fas fa-times" /></button>

        <div className="adm-wizard-header">
          <h2 className="bulk-title"><i className="fas fa-file-csv" /> {t('admin.bulkUsers.title')}</h2>
          <p className="bulk-sub">{t('admin.bulkUsers.wizardSubtitle')}</p>

          <div className="bulk-stepper" role="tablist" aria-label={t('admin.bulkUsers.title')}>
            {STEPS.map((wizardStep, index) => (
              <div
                key={wizardStep}
                className={`bulk-stepper-item ${index === stepIndex ? 'bulk-stepper-item--on' : ''} ${index < stepIndex ? 'bulk-stepper-item--done' : ''}`}
              >
                <span className="bulk-stepper-num">{index + 1}</span>
                <span className="bulk-stepper-label">{t(STEP_LABEL_KEYS[wizardStep])}</span>
              </div>
            ))}
          </div>
          <p className="bulk-step-of">{t('bulk.stepOf', { step: stepIndex + 1, total: STEPS.length })}</p>
        </div>

        {step === 'instructions' && <StepInstructions onNext={() => setStep('upload')} />}
        {step === 'upload' && (
          <StepUploadFile
            ou={ou}
            onOuChange={setOu}
            onBack={() => setStep('instructions')}
            onUploaded={(uploadedCsv) => { setCsv(uploadedCsv); setStep('review'); }}
          />
        )}
        {step === 'review' && (
          <StepReview
            csv={csv}
            ou={ou}
            importer={importer}
            onBack={() => setStep('upload')}
            onNext={() => { setPreviewResult(importer.data ?? null); setStep('confirm'); }}
          />
        )}
        {step === 'confirm' && previewResult && (
          <StepConfirm csv={csv} ou={ou} preview={previewResult} importer={importer} onBack={() => setStep('review')} onDone={onClose} />
        )}
      </div>
    </div>
  ), document.body);
}

export default AdminBulkUsersWizardModal;
